import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronDown, ChevronRight } from 'lucide-react';
import styles from './DashboardSidebar.module.css';

const DASHBOARD_MENU_ITEMS = [
    {
        id: 'overview',
        label: 'Overview',
        hasSubItems: true,
        expanded: false,
        subItems: [
            { id: 'dashboard', label: 'Dashboard', path: '/dashboard', active: false },
            { id: 'announcements', label: 'Announcements', path: '/dashboard/announcements', active: false }
        ]
    },
    {
        id: 'governance',
        label: 'Governance',
        hasSubItems: true,
        expanded: false,
        subItems: [
            { id: 'qbr', label: 'Shared Materials', path: '/dashboard/qbr', active: false }
        ]
    },
    {
        id: 'enablement',
        label: 'Enablement',
        hasSubItems: true,
        expanded: false,
        subItems: [
            { id: 'contacts', label: 'Contacts & On-Call', path: '/dashboard/contacts', active: false },
            { id: 'support', label: 'Submit Support Request', path: '/dashboard/submit-request', active: false }
        ]
    },
    {
        id: 'customer-engagement',
        label: 'Customer Engagement',
        hasSubItems: true,
        expanded: false,
        subItems: [
            { id: 'customer-360', label: '360 Customer View', path: '/service/customer-management', active: false }
        ]
    },
    {
        id: 'operations',
        label: 'Operations',
        hasSubItems: false
    },
    {
        id: 'financials',
        label: 'Financials',
        hasSubItems: false
    },
    {
        id: 'assets',
        label: 'Assets and Infrastructures',
        hasSubItems: false
    },
];

export const DashboardSidebar = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [menuItems, setMenuItems] = useState(DASHBOARD_MENU_ITEMS);

    useEffect(() => {
        // Set active state based on current path
        setMenuItems(items =>
            items.map(item => {
                if (item.hasSubItems && item.subItems) {
                    return {
                        ...item,
                        subItems: item.subItems.map(subItem => ({
                            ...subItem,
                            active: location.pathname === subItem.path
                        }))
                    };
                }
                return item;
            })
        );
    }, [location.pathname]);

    const toggleMenuItem = useCallback((itemId) => {
        setMenuItems(items =>
            items.map(item =>
                item.id === itemId
                    ? { ...item, expanded: !item.expanded }
                    : item
            )
        );
    }, []);

    const handleSubItemClick = useCallback((subItem) => {
        if (subItem.disabled) return;
        navigate(subItem.path);
        onClose();
    }, [navigate, onClose]);

    return (
        <aside className={`${styles.sidebar} ${!isOpen ? styles.collapsed : ''}`}>
            <div className={styles.header}>
                <button
                    className={styles.closeButton}
                    onClick={onClose}
                    aria-label="Close sidebar"
                >
                    <ChevronLeft size={20} />
                </button>
            </div>

            <nav className={styles.navigation}>
                {menuItems.map((item) => (
                    <div key={item.id} className={styles.menuItemWrapper}>
                        {/* Main menu item */}
                        <div
                            className={`${styles.menuItem} ${item.hasSubItems ? styles.expandable : styles.disabled}`}
                            onClick={() => item.hasSubItems && toggleMenuItem(item.id)}
                        >
                            <span className={styles.label}>{item.label}</span>
                            {item.hasSubItems && (
                                <span className={styles.expandIcon}>
                                    {item.expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                </span>
                            )}
                        </div>

                        {/* Sub-menu items */}
                        {item.hasSubItems && item.expanded && item.subItems && (
                            <div className={styles.subMenuItems}>
                                {item.subItems.map((subItem) => (
                                    <div
                                        key={subItem.id}
                                        className={`${styles.subMenuItem} ${subItem.active ? styles.active : ''} ${subItem.disabled ? styles.disabled : ''}`}
                                        onClick={subItem.disabled ? undefined : () => handleSubItemClick(subItem)}
                                    >
                                        <span className={styles.label}>{subItem.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </nav>
        </aside>
    );
};